const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface RequestOptions extends RequestInit {
  params?: Record<string, string>
}

let getTokenFn: (() => string | null) | null = null

export function setAuthTokenProvider(fn: () => string | null) {
  getTokenFn = fn
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options
  let url = `${API_BASE}${path}`

  if (params) {
    const searchParams = new URLSearchParams(params)
    url += `?${searchParams.toString()}`
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string>),
  }

  const token = getTokenFn?.()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  })

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '')
    throw new ApiError(response.status, response.statusText, errorBody)
  }

  // No body to parse: 204, or any empty 2xx response (e.g. registration returns
  // 201 Created with an empty body). Reading text first avoids json() throwing
  // "Unexpected end of JSON input" on an empty body.
  if (response.status === 204) {
    return undefined as T
  }
  const text = await response.text()
  if (!text) {
    return undefined as T
  }
  return JSON.parse(text) as T
}

/**
 * The API answers errors in three shapes:
 *   { error: "User already exists" }             — admin-backend logic failures
 *   { message: "Email already registered" }      — mobile-api handlers
 *   { errors: [{ msg: "…", param: "email" }] }   — express-validator field errors
 * Anything else (an HTML error page from a misrouted proxy, an empty body) falls
 * back to the status line — never surface markup to the user.
 *
 * Before this existed, `ApiError.message` was ALWAYS "API Error 400: Bad Request".
 * Every auth page renders `err.message`, so the server's real reason — "User
 * already exists", "Invalid verification code", "Cant find pending user" — was
 * read off the wire, stored on `.body`, and then thrown away. Sign-up failures
 * were indistinguishable from each other and gave the user nothing to act on.
 */
export function extractApiErrorMessage(status: number, statusText: string, body: string): string {
  const fallback = `API Error ${status}: ${statusText}`
  if (!body) return fallback
  try {
    const parsed = JSON.parse(body)
    if (typeof parsed?.error === 'string' && parsed.error.trim()) return parsed.error
    if (typeof parsed?.message === 'string' && parsed.message.trim()) return parsed.message
    if (Array.isArray(parsed?.errors)) {
      const msgs = parsed.errors
        .map((e: { msg?: string }) => e?.msg)
        .filter((m: unknown): m is string => typeof m === 'string' && m.trim().length > 0)
      if (msgs.length) return msgs.join('. ')
    }
  } catch {
    // Not JSON. Fall through to the status line rather than echoing a raw body.
  }
  return fallback
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: string,
  ) {
    super(extractApiErrorMessage(status, statusText, body))
    this.name = 'ApiError'
  }
}

export const api = {
  get: <T>(path: string, params?: Record<string, string>) =>
    request<T>(path, { method: 'GET', params }),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),

  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
}
